/**
 * The ground's mean tone (P2.12-T32, ADR-100's note under decision 42): the colour a chunk not drawn yet shows in place
 * of grey. Pure: pixels come in, a colour goes out.
 */

/**
 * The lowest alpha, of 255, at which a pixel counts as ground.
 */
export const OPAQUE_ALPHA = 128;

/**
 * A colour as three whole channels.
 */
export interface Tone {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

/**
 * The mean of the opaque pixels of an RGBA array, each channel rounded.
 *
 * @param data Pixels as red, green, blue and alpha bytes; bytes short of a whole pixel are ignored.
 * @returns The tone, or null when no pixel is opaque.
 */
export function meanTone(data: ArrayLike<number>): Tone | null {
  let r = 0;
  let g = 0;
  let b = 0;
  let count = 0;
  for (let i = 0; i + 3 < data.length; i += 4) {
    if ((data[i + 3] as number) >= OPAQUE_ALPHA) {
      r += data[i] as number;
      g += data[i + 1] as number;
      b += data[i + 2] as number;
      count += 1;
    }
  }
  return count === 0 ? null : { r: Math.round(r / count), g: Math.round(g / count), b: Math.round(b / count) };
}

/**
 * A tone as an opaque CSS colour.
 *
 * @param tone The tone.
 * @returns The colour text.
 */
export function toneColour(tone: Tone): string {
  return `rgb(${tone.r},${tone.g},${tone.b})`;
}
